import posthog from 'posthog-js'
import Session from 'supertokens-web-js/recipe/session'
// dbApi is intentionally a LAZY import inside identifyCurrentUser —
// importing it statically here creates a module-init cycle via
// dbApiService → superTokensApiService → router → usePostHog, which
// trips TDZ errors on the module-level const POSTHOG_KEY_UAT during
// test setup. The Vite "dynamic import will not move module into
// another chunk" warning for this one is acceptable; the cycle isn't.
import {
  trackException as aiTrackException,
  trackMetric as aiTrackMetric,
  setAuthenticatedUser as aiSetAuthenticatedUser,
} from '@/services/telemetryService'

// Hardcoded per-stage project keys. The earlier env-var based design let
// UAT inject its own values, but the `ENV_AZURE_UAT` GitHub secret had
// stale entries pointing at a different (US) PostHog project, so all
// events vanished into the wrong bucket. Pinning the keys per stage here
// keeps redeploys from silently re-routing analytics again. The on/off
// flag still comes from env so tests and local dev can override.
const POSTHOG_KEY_UAT  = 'phc_rfqm7CFtqMiGCeotdqHLVLfubeWoK2Ex4AqwUDG6XiU5'
const POSTHOG_KEY_PROD = 'phc_mWSZfWpwmmtXWmKeUgknGhdSyFs3oFwfBZdfR6ZKYxZV'
const POSTHOG_HOST = 'https://eu.i.posthog.com'

// Hard hostname gates. We send from UAT and Prod, but each goes to its
// OWN PostHog project so a Prod event can't contaminate the UAT funnel
// dashboards (and vice versa). Dev, local, and Azure preview slots send
// nothing.
function isUatHost(): boolean {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname.toLowerCase()
  return h.startsWith('uat.') || h.includes('-uat.') || h.includes('-uat-')
}
function isProdHost(): boolean {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname.toLowerCase()
  // Prod = the canonical FQDN with no env-prefix (uat./dev./localhost), and
  // not the raw "-dev-" / "-uat-" Azure container hostnames either.
  if (isUatHost()) return false
  if (h.startsWith('dev.') || h.includes('-dev.') || h.includes('-dev-')) return false
  if (h === 'localhost' || h.startsWith('127.') || h.endsWith('.local')) return false
  return h === 'eventhub.adg-campus.de' || h.endsWith('.eventhub.adg-campus.de')
}

let initialized = false

export function usePostHog() {
  const rawEnabled = import.meta.env.VITE_POSTHOG_ENABLED
  const onUat = isUatHost()
  const onProd = isProdHost()
  // Resolution order:
  //   1. env explicitly 'false'  → disabled (test stubs, local opt-out)
  //   2. env explicitly 'true'   → enabled (test stubs / explicit opt-in)
  //   3. hostname matches UAT/Prod → enabled (the only deployments we send from)
  //   4. otherwise                → disabled
  //
  // STEPS 1-2 ONLY APPLY TO VITEST AND LOCAL DEV. In a production build Vite
  // inlines import.meta.env.VITE_POSTHOG_ENABLED as a constant, so the minifier
  // folds both comparisons away and ships `isEnabled = onUat || onProd`. The
  // deployed app is therefore governed purely by hostname — setting the var on a
  // container app does nothing. Do NOT reintroduce it as a deploy-time switch
  // (the provisioner's hardcoded `false` once convinced an investigation that
  // prod analytics were off while they were running); if a real runtime toggle
  // is ever needed it must be read from window-injected config, not here.
  const isEnabled =
    rawEnabled === 'false' ? false :
    rawEnabled === 'true' ? true :
    (onUat || onProd)

  // Stage-specific key: prod takes priority (in case the hostname check
  // accidentally matches both, which shouldn't happen but is defensive).
  const key = onProd ? POSTHOG_KEY_PROD : POSTHOG_KEY_UAT

  if (isEnabled && !initialized) {
    posthog.init(key, {
      api_host: POSTHOG_HOST,
      // Pin SDK default behaviours to the 2026-01-30 set (autocapture +
      // pageview + heatmaps defaults frozen at that release). Without this
      // PostHog issues a console warning and resolves defaults at runtime,
      // which can drift across versions. See PostHog docs > "defaults".
      defaults: '2026-01-30',
      capture_pageview: false,
      capture_pageleave: true,
      persistence: 'localStorage',
      // Session recording snapshots the DOM on every mutation. On the
      // events page (one 6k-line SFC) that cost per keystroke was visible
      // as input lag when creating an internal event in prod. We get our
      // analytics value from explicit captures + autocapture clicks, not
      // recordings — disable to keep typing snappy.
      disable_session_recording: true,
    })
    initialized = true
    console.log('PostHog initialized', { host: POSTHOG_HOST, stage: onProd ? 'prod' : onUat ? 'uat' : 'env-override' })
  }

  const trackEvent = (eventName: string, properties?: Record<string, any>) => {
    if (isEnabled && initialized) {
      posthog.capture(eventName, properties)
    }
  }

  // Identify the signed-in user. Call once after auth resolves so every
  // subsequent event is attributed to a stable distinct_id (PostHog uses
  // anonymous IDs otherwise). userId should be the SuperTokens user UUID.
  // Pass any persistent user props (email, full_name, role) so cohorts
  // and filters in PostHog can slice by them without re-attaching per event.
  const identify = (userId: string | number, props?: Record<string, any>) => {
    if (isEnabled && initialized && userId) {
      posthog.identify(String(userId), props)
    }
  }

  // Logout — clears the local distinct_id so the next session starts fresh
  // and isn't attributed to the previous user (important on shared devices).
  const resetIdentity = () => {
    if (isEnabled && initialized) {
      posthog.reset()
    }
  }

  // Super-properties travel with EVERY subsequent event automatically — use
  // for slow-changing context (selected property_id, current user's role).
  // Stored client-side in localStorage; persists across page loads. Pass
  // `null` for a key to clear it.
  const setSuperProperties = (props: Record<string, any>) => {
    if (isEnabled && initialized) {
      posthog.register(props)
    }
  }

  // Convenience for tracking user-visible errors. Normalizes the payload so
  // dashboards can filter by `category` (mews_charge, save_event, …) and
  // shows the message + stack head without leaking auth tokens or PII.
  //
  // Two-sink fan-out by design:
  //   - PostHog gets the product-analytics view: `app_error` with category,
  //     identifiable to user via the distinct_id set during identify.
  //   - Application Insights gets the technical-deep-dive view: full
  //     trackException with operationId so frontend errors correlate with
  //     backend traces, AJAX request envelopes, and AI's exception index.
  //
  // AI fan-out is unconditional (not gated on PostHog isEnabled) so we
  // keep exception coverage in environments where PostHog is disabled
  // (e.g. prod, dev). telemetryService is a no-op when AI isn't set up.
  const trackError = (category: string, error: unknown, context?: Record<string, any>) => {
    const err = error instanceof Error ? error : new Error(String(error))
    // App Insights — always, even when PostHog is disabled.
    aiTrackException(err, { category, ...(context || {}) })
    // PostHog — only when enabled (UAT today).
    if (isEnabled && initialized) {
      posthog.capture('app_error', {
        category,
        message: err.message,
        // Cap stack at 800 chars — first frame is the most useful, the rest
        // bloats payloads and rarely tells us more than the message already does.
        stack: err.stack ? err.stack.slice(0, 800) : undefined,
        ...context,
      })
    }
  }

  // Stable timing helper — wraps an async operation, records duration in ms,
  // and emits a single `performance_timing` event tagged by name + any
  // context props (route, event_id, etc.). Used to attribute slow loads /
  // saves back to a user via the identified distinct_id.
  //
  // Fan-out: PostHog gets the discrete event (per-user funnel filters), AI
  // gets the same number as `trackMetric` (aggregated time series — AI's
  // Logs UI graphs metrics with min/avg/p95 over windows). AI is called
  // even when PostHog is disabled, so prod-only perf dashboards still work.
  const trackTiming = async <T>(name: string, fn: () => Promise<T>, context?: Record<string, any>): Promise<T> => {
    const t0 = performance.now()
    let ok = true
    let error: unknown = null
    try {
      return await fn()
    } catch (e) {
      ok = false
      error = e
      throw e
    } finally {
      const duration_ms = Math.round(performance.now() - t0)
      // App Insights — always
      aiTrackMetric(`perf.${name}`, duration_ms, { ok, ...(context || {}) })
      // PostHog — only when enabled
      if (isEnabled && initialized) {
        posthog.capture('performance_timing', {
          name,
          duration_ms,
          ok,
          ...(context || {}),
        })
      }
      if (!ok && error) {
        // Re-use trackError so AI and PostHog both get the exception with
        // a `perf:<name>` category, keeping perf-failure debugging in one
        // place across both backends.
        trackError(`perf:${name}`, error, context)
      }
    }
  }

  return {
    posthog: isEnabled ? posthog : null,
    isEnabled,
    trackEvent,
    identify,
    resetIdentity,
    setSuperProperties,
    trackError,
    trackTiming,
  }
}

// ─── Startup identify ──────────────────────────────────────────────
//
// Shared logic used by both `LoginView` (after a fresh signIn) and
// `auth.init()` (for sessions that survived a reload). Without this
// second entry point, returning users stay anonymous in PostHog because
// they never re-traverse the login screen.
//
// Best-effort: any failure (JWT not ready, profile row missing,
// PostgREST 5xx) is swallowed so analytics setup can't break the app.
//
// Session is statically imported above (no init cycle). dbApi stays
// lazy here because a static import would close the
// dbApiService → superTokensApiService → router → usePostHog cycle and
// break test module-init.
export async function identifyCurrentUser(): Promise<void> {
  try {
    const ph = usePostHog()
    const exists = await Session.doesSessionExist()
    if (!exists) return
    const stUserId = await Session.getUserId()
    if (!stUserId) return
    const { dbApi } = await import('@/services/dbApiService')
    const rows = await dbApi.get(`/users?supertokens_user_id=eq.${stUserId}&select=id,email,full_name,role,property_id&limit=1`)
    const u = rows?.[0] || {}

    // App Insights — always identify the user (even when PostHog is off)
    // so exception/AJAX envelopes get user_role, user_property_id, etc.
    // as custom dimensions for Kusto queries against the AI workspace.
    aiSetAuthenticatedUser(stUserId, {
      email: u.email,
      role: u.role,
      property_id: u.property_id,
      full_name: u.full_name,
    })

    // PostHog — only when PostHog is enabled (UAT today).
    if (ph.isEnabled) {
      ph.identify(stUserId, {
        email: u.email,
        full_name: u.full_name,
        role: u.role,
        db_user_id: u.id,
        property_id: u.property_id,
      })
      if (u.role || u.property_id) {
        ph.setSuperProperties({
          user_role: u.role,
          primary_property_id: u.property_id,
        })
      }
    }
  } catch (err) {
    // Identify is best-effort — never block the UI on it.
    console.warn('identifyCurrentUser failed (non-fatal):', err)
  }
}
